/**
 * Parser del QR de documentos electrónicos DIAN, en sus dos variantes reales: el string multilínea
 * «Clave: valor» del anexo técnico con URL final, y la URL sola con el CUFE en `documentkey`. El
 * contenido es DATO NO CONFIABLE: formatos cerrados y siempre como texto plano.
 */

export interface QrDianCampos {
  tipo: 'campos';
  cufe: string;
  legacy: boolean; // CUFE de 64 hex (anexos viejos)
  nitEmisor?: string;
  docAdquiriente?: string;
  fecha?: string;
  valorSinImpuestos?: number;
  valorIva?: number;
  /** Lo que el anexo llama `ValOtroIm`: todo tributo que no es el IVA, en un solo monto. */
  valorOtrosImpuestos?: number;
  valorTotal?: number;
  url?: string;
}

export interface QrDianSoloUrl {
  tipo: 'solo_url';
  cufe: string;
  legacy: boolean;
  url: string;
  habilitacion: boolean; // ambiente -hab de la DIAN
}

interface QrNoDian {
  tipo: 'no_dian';
}

export type QrDian = QrDianCampos | QrDianSoloUrl | QrNoDian;

const RE_DOCUMENTKEY = /documentkey(?:=|%3[dD])([0-9a-fA-F]{96}|[0-9a-fA-F]{64})/;
const RE_HEX = /^[0-9a-fA-F]+$/;
const RE_URL_DIAN = /https?:\/\/catalogo-vpfe(-hab)?\.dian\.gov\.co\/\S+/i;

function parsearNumero(v: string): number | undefined {
  const limpio = v.replace(/[^\d.,-]/g, '').replace(/,/g, '');
  if (limpio === '') return undefined;
  const n = Number(limpio);
  return Number.isFinite(n) ? Math.round(n) : undefined;
}

export function parsearQrDian(crudo: string): QrDian {
  const texto = (crudo ?? '').trim();
  if (texto.length < 10 || texto.length > 4000) return { tipo: 'no_dian' };

  // Variante 2: URL con documentkey (en cualquier parte, incluida la de login con ReturnUrl)
  const urlMatch = texto.match(RE_DOCUMENTKEY);
  const urlDian = texto.match(RE_URL_DIAN)?.[0];

  // Variante 1: un par por línea. Los proveedores emiten "Clave: valor" y "Clave=valor";
  // manda el separador que aparezca primero para no partir "HorFac=10:57:42-05:00" por su hora.
  const campos = new Map<string, string>();
  for (const lineaCruda of texto.split(/\r?\n/)) {
    const linea = lineaCruda.trim();
    const dosPuntos = linea.indexOf(':');
    const igual = linea.indexOf('=');
    const candidatos = [dosPuntos, igual].filter((i) => i > 0);
    const sep = candidatos.length ? Math.min(...candidatos) : -1;
    if (sep > 0 && sep < 20) {
      const clave = linea.slice(0, sep).trim().toLowerCase();
      const valor = linea.slice(sep + 1).trim();
      if (clave && valor) campos.set(clave, valor);
    }
  }

  const cufeCampo = campos.get('cufe') ?? campos.get('cude');
  const cufeValido =
    cufeCampo && RE_HEX.test(cufeCampo) && (cufeCampo.length === 96 || cufeCampo.length === 64)
      ? cufeCampo
      : undefined;

  if (cufeValido || (campos.has('numfac') && urlMatch)) {
    const cufe = cufeValido ?? urlMatch![1]!;
    return {
      tipo: 'campos',
      cufe: cufe.toLowerCase(),
      legacy: cufe.length === 64,
      nitEmisor: campos.get('nitfac')?.replace(/\D/g, '') || undefined,
      docAdquiriente: campos.get('docadq')?.replace(/\D/g, '') || undefined,
      fecha: campos.get('fecfac'),
      valorSinImpuestos: parsearNumero(campos.get('valfac') ?? ''),
      valorIva: parsearNumero(campos.get('valiva') ?? ''),
      valorOtrosImpuestos: parsearNumero(campos.get('valotroim') ?? ''),
      valorTotal: parsearNumero(campos.get('valtolfac') ?? campos.get('valtotfac') ?? ''),
      url: urlDian,
    };
  }

  if (urlMatch && urlDian) {
    const cufe = urlMatch[1]!;
    return {
      tipo: 'solo_url',
      cufe: cufe.toLowerCase(),
      legacy: cufe.length === 64,
      url: urlDian,
      habilitacion: /catalogo-vpfe-hab\./i.test(urlDian),
    };
  }

  return { tipo: 'no_dian' };
}

/** URL de consulta pública para un CUFE/CUDE (producción DIAN) */
export function urlConsultaDian(cufe: string): string {
  return `https://catalogo-vpfe.dian.gov.co/document/searchqr?documentkey=${encodeURIComponent(cufe)}`;
}
